/**
 * Video Player for displaying live and historical video streams
 * via the custom Hellometer video streaming solution.
 */
import { useRef, useEffect, useState } from 'react';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import { useCamera } from '../../hooks/useCamera';
import {
  getHistoricalManifestUrl,
  getLiveManifestUrl,
  getStreamTimes,
} from '../../utils/video';
import type { Location } from '../../types/Location';
import VideoPlayer from './VideoPlayer';

export default function VideoPlayerCustom({
  location,
  cameraId,
}: {
  location: Location;
  cameraId: string;
}): JSX.Element {
  const [player, setPlayer] = useState<Player | null>(null);
  const [manifestUrl, setManifestUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // ✅ Get useCamera values at the top level
  const { isLive, playbackSpeed, videoDateTime, isPlaying, setIsPlaying, setVideoDateTime } =
    useCamera();

  const videoDateTimeRef = useRef(videoDateTime);
  videoDateTimeRef.current = videoDateTime; // Always keep it up to date

  // On load, initialize videojs
  useEffect(() => {
    if (videoRef.current === null) {
      return;
    }

    const playerObject = videojs(videoRef.current, {
      autoplay: true,
      muted: true,
      playsinline: true,
      controls: true,
      preload: 'auto',
      userActions: {
        click: false,
      },
      html5: {
        vhs: {
          overrideNative: true,
        },
      },
    });

    setPlayer(playerObject);

    let retryCount = 0;
    const maxRetries = 100;
    let retryTimeout: NodeJS.Timeout | null = null; // To track retry timeout

    const retryWithDelay = () => {
      if (retryCount >= maxRetries) {
        console.error('Max retries reached. Stopping retry attempts.');
        return;
      }

      retryCount++;
      console.log(`Retrying to load the video... Attempt ${retryCount}`);

      // Clear previous timeout (if any) to avoid stacking retries
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }

      retryTimeout = setTimeout(() => {
        playerObject.load();
        playerObject.play()?.catch((error) => {
          console.error('Error during retry:', error);
        });
      }, 2000); // Set a 2-second delay before retrying
    };

    // Handle video ended event
    const HandleEnded = () => {
      console.log('Video ended, fetching next playlist...', isLive);

        const videoDuration = player.duration(); // Returns duration in seconds

        if (!videoDuration || isNaN(videoDuration)) {
          console.warn('Unable to retrieve video duration, using fallback.');
          return;
        }

        console.log('Current videoDateTime:', videoDateTime);
        console.log('Current video duration:', videoDuration, 'seconds');

        // Increment videoDateTime by the actual video duration
        const nextVideoDateTime = new Date(
          new Date(videoDateTimeRef.current).getTime() + videoDuration * 1000 // Convert seconds to milliseconds
        );

        console.log('Fetching next segment for time:', nextVideoDateTime);
        setVideoDateTime(nextVideoDateTime); // Update the videoDateTime in your context or state
    };

    playerObject.on('ended', HandleEnded);

    // Add listeners
    playerObject.on('error', () => {
      retryWithDelay(); // Retry with delay on error
    });

    playerObject.on('loadedmetadata', () => {
      const w = wrapperRef.current?.clientWidth;
      playerObject.width(w);
      retryCount = 0; // Reset retry count on successful load
    });

    // Cleanup
    return () => {
      if (player !== null) {
        player.dispose();
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout); // Clean up the timeout on unmount
      }
    };
  }, [videoRef.current]);

  // When the manifest URL changes, update the player source.
  useEffect(() => {
    if (player === null || manifestUrl === null) {
      return;
    }
    player.src({
      src: manifestUrl,
      type: 'application/x-mpegURL',
    });
    setIsPlaying(true);
  }, [manifestUrl, player]);

  // Go to live or historical video.
  useEffect(() => {
    if (isLive) {
      getLiveManifestUrl(location.id, cameraId)
        .then((manifestUrl: string | null) => {
          setManifestUrl(manifestUrl);
        })
        .catch((error) => {
          console.error('Error getting live video', error);
        });
    } else {
      const { startTime, endTime } = getStreamTimes(
        videoDateTime,
        location.timezone
      );
      getHistoricalManifestUrl(location.id, cameraId, startTime, endTime)
        .then((manifestUrl: string | null) => {
          setManifestUrl(manifestUrl);
        })
        .catch((error) => {
          console.error('Error getting historical video', error);
        });
    }
  }, [player, isLive, videoDateTime, location, cameraId]);

  // Change video speed.
  useEffect(() => {
    if (player === null) {
      return;
    }
    try {
      player.playbackRate(playbackSpeed);
      player.defaultPlaybackRate(playbackSpeed);
    } catch (err) {
      console.log(err);
    }
  }, [playbackSpeed]);

  // Play or pause the video based on the isPlaying prop.
  useEffect(() => {
    if (player === null) {
      return;
    }

    if (!isPlaying) {
      player.pause();
    } else {
      player.play()?.catch((err) => {
        console.log(err);
      });
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!isLive) {
      return;
    }
    // Refresh every 60 seconds.
    const interval = setInterval(() => {
      player?.src({
        src: manifestUrl,
        type: 'application/x-mpegURL',
      });

      console.log('Refreshing video.');
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  });

  return (
    <VideoPlayer
      cameraId={cameraId}
      videoRef={videoRef}
      wrapperRef={wrapperRef}
    />
  );
}
